import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Pages
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Events from './pages/Events';
import Halls from './pages/Halls';
import Bookings from './pages/Bookings';
import Notifications from './pages/Notifications';
import NotificationsHistory from './pages/NotificationsHistory';
import Users from './pages/Users';
import Gallery from './pages/Gallery';
import Hadith from './pages/Hadith';
import Azkar from './pages/Azkar';
import Duas from './pages/Duas';
import PrivacyPolicy from './pages/PrivacyPolicy';
import AppSettings from './pages/AppSettings';

// Placeholder pages

const Speeches = () => (
  <div className="flex items-center justify-center min-h-96">
    <div className="text-center">
      <h1 className="text-2xl font-bold text-text-primary mb-2">إدارة الخطب</h1>
      <p className="text-text-secondary">قريباً - إدارة تسجيلات خطب الجمعة</p>
    </div>
  </div>
);

// Create a query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-primary-dark">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-gold"></div>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// Public Route Component (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-primary-dark">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-gold"></div>
      </div>
    );
  }

  return isAuthenticated ? <Navigate to="/" replace /> : children;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <div className="App">
            <Routes>
              {/* Public Routes */}
              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <Login />
                  </PublicRoute>
                }
              />
              <Route
                path="/reset-password"
                element={<ResetPassword />}
              />

              {/* Protected Routes */}
              <Route 
                path="/" 
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/events" 
                element={
                  <ProtectedRoute>
                    <Events />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/halls" 
                element={
                  <ProtectedRoute>
                    <Halls />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/bookings" 
                element={
                  <ProtectedRoute>
                    <Bookings />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/gallery" 
                element={
                  <ProtectedRoute>
                    <Gallery />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/speeches" 
                element={
                  <ProtectedRoute>
                    <Speeches />
                  </ProtectedRoute>
                } 
              />
              <Route
                path="/notifications"
                element={
                  <ProtectedRoute>
                    <Notifications />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/notifications/history"
                element={
                  <ProtectedRoute>
                    <NotificationsHistory />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/users"
                element={
                  <ProtectedRoute>
                    <Users />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/hadith"
                element={
                  <ProtectedRoute>
                    <Hadith />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/azkar"
                element={
                  <ProtectedRoute>
                    <Azkar />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/duas"
                element={
                  <ProtectedRoute>
                    <Duas />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/privacy-policy"
                element={<PrivacyPolicy />}
              />
              <Route
                path="/app-settings"
                element={
                  <ProtectedRoute>
                    <AppSettings />
                  </ProtectedRoute>
                }
              />

              {/* Fallback Route */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>

            {/* Toast Notifications */}
            <Toaster
              position="top-center"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#333333',
                  color: '#FFFFFF',
                  border: '1px solid #404040',
                  direction: 'rtl',
                },
                success: {
                  iconTheme: {
                    primary: '#D4AF37',
                    secondary: '#1A1A1A',
                  },
                },
              }}
            />
          </div>
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;